import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { ErrorResponse } from "../../../Models/Error.ts";
import { callErrorToast } from "../../../utilities/utilities.ts";
import { FeedbackService } from "../../../services/registry/feedbacks.ts";
import { Feedback } from "../../../Models/Feedback.ts";

const feedbackService = new FeedbackService();

interface HandlerProviderProps {
  id: string;
  children: ReactNode;
  callback?: () => void;
  autosave?: boolean;
}

interface HandlerContextValue {
  data: Partial<Feedback>;
  error: ErrorResponse<Feedback> | null;
  loader: boolean;
  callback?: () => void;
  id: string;
}

const HandlerContext = createContext<HandlerContextValue | undefined>(
  undefined
);

const HandlerProvider: React.FC<HandlerProviderProps> = ({
  id,
  children,
  callback,
}) => {
  const [data, setData] = useState<Partial<Feedback>>({});
  const [loader, setLoader] = useState<boolean>(true);
  const [error, setError] = useState<ErrorResponse<Feedback> | null>(null);

  const getFeedback = (id: string): void => {
    feedbackService
      .get(id)
      .then((res) => {
        setData(res.data);
        setLoader(false);
      })
      .catch((err) => {
        callErrorToast(err);
        setLoader(false);
        setError(err);
      });
  };

  useEffect(() => {
    if (id) {
      getFeedback(id);
    } else {
      setLoader(false);
    }
  }, [id]);

  return (
    <HandlerContext.Provider
      value={{
        data,
        error,
        loader,
        callback,
        id,
      }}
    >
      {children}
    </HandlerContext.Provider>
  );
};

const useHandlerContext = (): HandlerContextValue => {
  const context = useContext(HandlerContext);
  if (!context) {
    throw new Error(
      "useHandlerContext must be used within a HandlerFeedbackProvider"
    );
  }
  return context;
};

export { HandlerProvider, useHandlerContext };
